import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  message,
  Select,
  Typography,
  Upload,
} from "antd";
import "./programEdit.scss";

import { fileUpload, programDetail, programUpdate } from "../../../apis/admin";
import { replaceUrlByParams } from "../../../utils/api";
import { PAGE } from "../../../utils/constants";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { AppContext } from "../../../App";
import { paymentMethodText } from "../../../components/paymentStatusTemplate/paymentStatusTemplate";
import { FormContext } from "antd/lib/form/context";

function ProgramEditPage() {
  let history = useHistory();
  const { pid } = useParams();
  const [form] = Form.useForm();
  const { Title } = Typography;
  const { Option } = Select;
  const { TextArea } = Input;
  const [programDetailField, setProgramDetailField] = useState([]);
  const [fileRes, setFileRes] = useState({});
  const dividerConfig = { orientation: "left" };
  const { appSetting, setLoading, adminSetting } = useContext(AppContext);

  useEffect(() => {
    getProgramDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps,
  }, []);

  const getProgramDetail = async () => {
    setLoading(true);
    try {
      const res = await programDetail(pid);
      if (res.error) {
        throw new Error(`${res.error.msgCode} - ${res.error.message}`);
      } else {
        setProgramDetailField(res.configuration.checkResult.fields);

        const fieldsToObject = res.configuration.checkResult.fields.reduce(
          (acc, curr) => {
            return {
              ...acc,
              [curr.id]: {
                name: curr.name,
                optional: curr.optional,
              },
            };
          },
          {}
        );

        const checkResultValue = Object.keys(
          res.configuration.checkResult
        ).reduce((acc, curr) => {
          return {
            ...acc,
            [curr]: res.configuration.checkResult[curr],
            ...{ fields: fieldsToObject },
          };
        }, {});

        const mapToValue = {
          ...res.configuration,
          ...{ checkResult: checkResultValue },
        };

        form.setFieldsValue(mapToValue);

        if (
          mapToValue.logo ||
          mapToValue.image ||
          mapToValue.receipt.backgroundLogo
        ) {
          const logo = mapToValue.logo
            ? {
                url: mapToValue.logo,
              }
            : null;
          const image = mapToValue.image
            ? {
                url: mapToValue.image,
              }
            : null;

          const backgroundLogo = mapToValue.receipt.backgroundLogo
            ? {
                url: mapToValue.receipt.backgroundLogo,
              }
            : null;

          setFileRes({
            logo,
            image,
            backgroundLogo,
          });
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  function valueToUpdate(value) {
    //get "fields" to the submit
    const fields = Object.keys(value.checkResult.fields).map((fieldId) => {
      return {
        ...programDetailField.find((e) => e.id === fieldId),
        name: value.checkResult.fields[fieldId].name,
        optional: value.checkResult.fields[fieldId].optional,
      };
    });
    return {
      ...value,
      ...{
        checkResult: Object.keys(value.checkResult).reduce((acc, curr) => {
          return {
            ...acc,
            [curr]: value.checkResult[curr],
            ...{ fields: fields },
          };
        }, {}),
      },
    };
  }

  const upload = async (req, key) => {
    const { onSuccess, onError, file } = req;
    try {
      const res = await fileUpload(pid, file);
      if (res.error) {
        throw new Error(`${res.error.msgCode} - ${res.error.message}`);
      } else {
        onSuccess(null, file);
        form.setFieldsValue(
          typeof key === "object"
            ? key.reverse().reduce((acc, curr) => ({ [curr]: acc }), res.key)
            : { [key]: res.key }
        );
      }
    } catch (e) {
      onError();
    }
  };

  function config(name, key) {
    return {
      customRequest: (req) => upload(req, key ? key : name),
      beforeUpload: beforeUpload,
      onChange(info) {
        const { status, originFileObj } = info.file;
        if (status === "uploading") {
          setFileRes({
            ...fileRes,
            [name]: {
              loading: true,
            },
          });
          return;
        }
        if (status === "done") {
          message.success(`上傳圖片成功`);
          getBase64(originFileObj, (imageUrl) =>
            setFileRes({
              ...fileRes,
              [name]: {
                ...fileRes[name],
                loading: false,
                url: imageUrl,
              },
            })
          );
        } else if (status === "error") {
          message.error(`上傳圖片失敗，請重新嘗試`);
          setFileRes({
            ...fileRes,
            [name]: {
              ...fileRes[name],
              loading: false,
            },
          });
        }
      },
    };
  }

  function getBase64(img, callback) {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  function beforeUpload(file) {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("請上傳JPG/PNG格式圖片");
    }
    return isJpgOrPng;
  }

  const onFinish = async (value) => {
    value = Object.keys(value)
      .filter((e) => value[e] !== undefined)
      .reduce((acc, curr) => {
        //filter unless key
        return {
          ...acc,
          [curr]: value[curr],
        };
      }, {});

    // const mapFields = valueToUpdate(value) //when "fields" needed
    try {
      let res = await programUpdate(pid, value); //replace getFields to mapFields
      if (res.error) {
        throw new Error(`${res.error.msgCode} - ${res.error.message}`);
      } else {
        message.success("成功儲存", 2, () =>
          history.push(
            `${replaceUrlByParams(PAGE.ADMIN_PROGRAM_DETAIL, { ":pid": pid })}`
          )
        );
      }
    } catch (e) {
      console.log(e);
    }
  };
  const onFinishFailed = (errorInfo) => {
    // console.log('Failed:', errorInfo);
  };

  return (
    <div className="program-edit-page">
      <Title level={4}>編輯課程</Title>
      <Form
        layout="vertical"
        hideRequiredMark={true}
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Divider {...dividerConfig}>課程設定</Divider>
        <div className="form-section">
          <Form.Item
            label="課程名稱"
            name="title"
            rules={[{ required: true, message: "請輸入課程名稱" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="課程編號"
            name="referenceId"
            rules={[{ required: true, message: "請輸入課程編號" }]}
          >
            <Input maxLength={10} />
          </Form.Item>
        </div>
        <div className="form-section">
          <div className="bundle">
            <Form.Item label="Logo" name="logo">
              <Input hidden />
            </Form.Item>

            <Upload
              listType="picture-card"
              showUploadList={false}
              accept="image/x-png,image/gif,image/jpeg"
              {...config("logo")}
            >
              {fileRes.logo && fileRes.logo.url ? (
                <img
                  src={fileRes.logo.url}
                  alt="logo"
                  style={{ width: "100%" }}
                />
              ) : (
                <div>
                  {fileRes.logo && fileRes.logo.loading ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusOutlined />
                  )}
                  <div className="ant-upload-text">上傳</div>
                </div>
              )}
            </Upload>
          </div>
          <div className="bundle">
            <Form.Item label="横幅" name="image">
              <Input hidden />
            </Form.Item>

            <Upload
              listType="picture-card"
              showUploadList={false}
              accept="image/x-png,image/gif,image/jpeg"
              {...config("image")}
            >
              {fileRes.image && fileRes.image.url ? (
                <img
                  src={fileRes.image.url}
                  alt="logo"
                  style={{ width: "100%" }}
                />
              ) : (
                <div>
                  {fileRes.image && fileRes.image.loading ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusOutlined />
                  )}
                  <div className="ant-upload-text">上傳</div>
                </div>
              )}
            </Upload>
          </div>
        </div>
        <div className="form-section full">
          <Form.Item label="課程描述" name="description">
            <TextArea rows={4} />
          </Form.Item>
        </div>

        <div className="form-section">
          <Form.Item
            label="接受付款"
            name={["payment", "enabled"]}
            rules={[{ required: true, message: "請輸入接受付款" }]}
          >
            <Select>
              <Option value={true}>是</Option>
              <Option value={false}>否</Option>
            </Select>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, curr) => curr.payment.paymentMethod}
            noStyle
          >
            {({ getFieldValue }) => (
              <Form.Item
                label="接受付款方式"
                name={["payment", "paymentMethod"]}
                rules={[{ required: true, message: "請選擇接受付款" }]}
              >
                {
                  <Checkbox.Group
                    options={adminSetting.enabledPaymentMethod.map(
                      ({ id, enabled }) => {
                        const paymentMethod = getFieldValue([
                          "payment",
                          "paymentMethod",
                        ]);
                        const disabled =
                          !enabled &&
                          paymentMethod &&
                          !paymentMethod.includes(id);
                        return {
                          label: paymentMethodText(id),
                          value: id,
                          disabled,
                        };
                      }
                    )}
                  />
                }
              </Form.Item>
            )}
          </Form.Item>

          <Form.Item
            label="課程狀態"
            name="activated"
            rules={[{ required: true, message: "請輸入課程狀態" }]}
          >
            <Select>
              <Option value={true}>進行中</Option>
              <Option value={false}>已完結</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="報名名額"
            name="maxApplicationCount"
            rules={[{ required: true, message: "請輸入報名名額" }]}
          >
            <Input pattern="[0-9]*" />
          </Form.Item>

          <Form.Item
            label="課程完結訊息"
            name="deactivatedMsg"
            rules={[{ required: true, message: "請輸入課程完結訊息" }]}
          >
            <Input />
          </Form.Item>
        </div>

        <div className="form-section full">
          <Form.Item label="收據備註" name={["receipt", "remarks"]}>
            <TextArea rows={4} />
          </Form.Item>
        </div>

        <div className="form-section">
          <div className="bundle">
            <Form.Item label="水印背景" name={["receipt", "backgroundLogo"]}>
              <Input hidden />
            </Form.Item>

            <Upload
              listType="picture-card"
              showUploadList={false}
              accept="image/x-png,image/gif,image/jpeg"
              {...config("backgroundLogo", ["receipt", "backgroundLogo"])}
            >
              {fileRes.backgroundLogo && fileRes.backgroundLogo.url ? (
                <img
                  src={fileRes.backgroundLogo.url}
                  alt="backgroundLogo"
                  style={{ width: "100%" }}
                />
              ) : (
                <div>
                  {fileRes.backgroundLogo && fileRes.backgroundLogo.loading ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusOutlined />
                  )}
                  <div className="ant-upload-text">上傳</div>
                </div>
              )}
            </Upload>
          </div>
          <Form.Item
            label="收據頁尾"
            name={["receipt", "footer"]}
            rules={[{ required: true, message: "請輸入課程完結訊息" }]}
          >
            <Input />
          </Form.Item>
        </div>

        <Divider {...dividerConfig}>查詢表格</Divider>

        <div className="form-section">
          <Form.Item
            label="報名狀態"
            name="enrolmentEnabled"
            rules={[{ required: true, message: "請輸入報名狀態" }]}
          >
            <Select>
              <Option value={true}>接受報名</Option>
              <Option value={false}>已完結</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="報名完結訊息"
            name="enrolmentDisabledMsg"
            rules={[{ required: true, message: "請輸入報名完結訊息" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="報名按鈕文字"
            name="enrolButton"
            rules={[{ required: true, message: "請輸入報名按鈕文字" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="查詢結果標題"
            name={["checkResult", "name"]}
            rules={[{ required: true, message: "請輸入查詢結果標題" }]}
          >
            <Input />
          </Form.Item>

          {/*when support fields*/}
          {/*{*/}
          {/*    programDetailField.map((field, i) => {*/}
          {/*        const optional = <Form.Item*/}
          {/*                            label='選填'*/}
          {/*                            name={['checkResult', 'fields', field.id, 'optional']}*/}
          {/*                        >*/}
          {/*                            <Select*/}
          {/*
                {/*                            >*/}
          {/*                                <Option value={true}>是</Option>*/}
          {/*                                <Option value={false}>否</Option>*/}
          {/*                            </Select>*/}
          {/*                        </Form.Item>*/}
          {/*        switch(field.type){*/}
          {/*            case 'text':*/}
          {/*                return (<div key={i}>*/}
          {/*                    <Form.Item*/}
          {/*                        label={`查詢欄目 ${i+1}`}*/}
          {/*                        name={['checkResult', 'fields', field.id, 'name']}*/}
          {/*                    >*/}
          {/*                        <Input*/}
          {/*
                {/*                        />*/}
          {/*                    </Form.Item>*/}
          {/*                    {optional}*/}
          {/*                </div>)*/}
          {/*            default: return <></>*/}
          {/*        }*/}
          {/*    })*/}
          {/*}*/}

          <Form.Item
            label="提交查詢按鈕文字"
            name={["checkResult", "submitButton"]}
            rules={[{ required: true, message: "請輸入提交查詢按鈕文字" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="申請成功標題"
            name={["applicationSuccess", "title"]}
            rules={[{ required: true, message: "請輸入申請成功標題" }]}
          >
            <TextArea rows={4} />
          </Form.Item>
        </div>
        <div className="form-section full">
          <Form.Item
            label="申請成功描述"
            name={["applicationSuccess", "description"]}
          >
            <TextArea rows={4} />
          </Form.Item>
        </div>
        <div className="form-section"></div>

        <div className="button-container">
          <Button onClick={() => history.goBack()}>取消</Button>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              儲存
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}

export default ProgramEditPage;
